import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

/**
 * ConfirmationDialog — centered modal "Are you sure?" prompt.
 *
 * Used for any destructive or hard-to-reverse action across the app.
 * Replaces inline two-click confirmations and browser `confirm()` with a
 * deliberate, centered, visually distinct modal so an accidental click in
 * a multi-task workflow doesn't slide a destructive change through.
 *
 * Pattern (D-050):
 *   - Centered, modal overlay (Radix Dialog).
 *   - "Are you sure?" header in red, bold.
 *   - Specific question / body text.
 *   - Confirm button on the right with red background (variant="danger")
 *     or accent orange (variant="warning") depending on the action.
 *   - Cancel button beside it. Esc / click-outside / X all dismiss.
 *
 * Optional typed-phrase gate (D-073, ARCH §10.4): when `requireTypedPhrase`
 * is set, the dialog renders a text input below the description; the
 * Confirm button stays disabled until the user types the exact phrase
 * (case-insensitive trim). Used for project delete where ARCH §10.4
 * specifies typing the word "delete" before the action commits.
 *
 * Used by:
 *   - DocumentsTrackerPage delete row + status change
 *   - FolderLocationSection migrate-to-convention (D-069)
 *   - ArchiveSection archive + unarchive (D-070)
 *   - ProjectSettingsPage DangerZoneSection delete (D-073, with typed-phrase)
 */

export type ConfirmVariant = 'danger' | 'warning';

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'danger',
  pending = false,
  requireTypedPhrase,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  variant?: ConfirmVariant;
  pending?: boolean;
  /** When set, gate the Confirm button on the user typing this phrase exactly. */
  requireTypedPhrase?: string;
}) {
  // Typed-phrase state — reset every time the dialog opens so reusing the
  // same dialog instance for multiple confirmations doesn't carry over the
  // previous typed value.
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setTyped('');
      // Tiny delay so Radix's autoFocus on the Confirm button doesn't
      // race the input focus on the same render frame.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);
  const phraseMatches =
    !requireTypedPhrase ||
    typed.trim().toLowerCase() === requireTypedPhrase.trim().toLowerCase();
  const confirmDisabled = pending || !phraseMatches;
  const accentColor =
    variant === 'danger' ? 'var(--color-danger)' : 'var(--color-warning)';
  const accentSoft =
    variant === 'danger'
      ? 'var(--color-danger-soft)'
      : 'var(--color-warning-soft)';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[60]"
          style={{ background: 'rgba(28, 38, 56, 0.55)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2 w-[min(440px,92vw)] border-2 rounded-lg shadow-2xl"
          // Dialog body uses the redesign gray palette (gray-100 panel,
          // gray-200 footer band) instead of the global cream surface
          // tokens. Radix Dialog renders into a Portal so page-scoped
          // CSS-variable overrides don't reach it — overriding inline on
          // the dialog itself is the cleanest way to apply the gray
          // scheme site-wide without flipping the global theme.
          style={{ borderColor: accentColor, background: '#F3F4F6' }}
          aria-describedby={description ? 'confirm-dialog-desc' : undefined}
        >
          <header
            className="px-5 py-3 border-b-2 flex items-center justify-between"
            style={{ borderColor: accentColor, background: accentSoft }}
          >
            <Dialog.Title
              className="text-base font-bold uppercase tracking-wide"
              style={{ color: accentColor }}
            >
              Are you sure?
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-ink-2 hover:text-ink text-sm px-2 py-0.5 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Cancel"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="p-5">
            <p className="text-base font-medium text-ink">{title}</p>
            {description && (
              <p
                id="confirm-dialog-desc"
                className="mt-2 text-sm text-ink-2"
              >
                {description}
              </p>
            )}
            {requireTypedPhrase && (
              <div className="mt-3 flex flex-col gap-1.5">
                <label
                  htmlFor="confirm-dialog-typed-phrase"
                  className="text-xs uppercase tracking-wide font-semibold text-ink-2"
                >
                  Type{' '}
                  <span
                    className="num font-bold"
                    style={{ color: accentColor }}
                  >
                    {requireTypedPhrase}
                  </span>{' '}
                  to confirm
                </label>
                <input
                  ref={inputRef}
                  id="confirm-dialog-typed-phrase"
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  className="w-full text-sm text-ink border-2 rounded px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
                  style={{ borderColor: accentColor }}
                />
              </div>
            )}
          </div>

          <footer
            className="px-5 py-3 border-t flex items-center justify-end gap-2"
            style={{ background: '#E5E7EB', borderColor: '#D1D5DB' }}
          >
            <Dialog.Close asChild>
              <button
                type="button"
                className="px-3 py-1.5 rounded text-sm border hover:bg-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-accent"
                style={{ borderColor: '#D1D5DB' }}
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled}
              // Auto-focus only when there's no typed-phrase gate. With a
              // gate, the input gets focus instead so the user can type
              // immediately.
              autoFocus={!requireTypedPhrase}
              className="px-3 py-1.5 rounded text-sm font-medium text-surface focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
              style={{ background: accentColor }}
            >
              {pending ? 'Working…' : confirmLabel}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
